import { useState } from "react"
import { LoaderCircleIcon, WrenchIcon } from "lucide-react"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"

export interface LiveCall {
  call: PlaybookToolCall
  done: boolean
}

const CHIP = "inline-flex items-center gap-1 rounded border bg-background px-1.5 py-0.5 font-mono hover:bg-muted"

export function track(list: LiveCall[] | null, call: PlaybookToolCall, done: boolean): LiveCall[] {
  const calls = list || []
  const index = calls.findIndex(c => !c.done && c.call.name === call.name)
  if (!done || index < 0) return [...calls, { call, done }]
  return calls.map((c, i) => i === index ? { call, done } : c)
}

function running(name: string) {
  if (name.startsWith("search_")) return "Searching…"
  if (name.startsWith("generate_")) return "Writing…"
  if (name.startsWith("pdf_")) return "Making the PDF…"
  return `Running ${name}…`
}

function summary(call: PlaybookToolCall) {
  if (Array.isArray(call.result)) return `${call.result.length} results`
  if (call.result === undefined) return undefined
  const text = typeof call.result === "string" ? call.result : JSON.stringify(call.result)
  return text.length > 24 ? `${text.slice(0, 24)}…` : text
}

export function Activity({ calls, thinking }: { calls: LiveCall[], thinking?: boolean }) {
  const [open, setOpen] = useState<PlaybookToolCall | null>(null)
  const shown = calls.filter(c => !c.call.name.startsWith("transition_"))
  const busy = shown.filter(c => !c.done)
  if (!shown.length && !thinking) return null

  return <div className="flex flex-col gap-1 text-xs text-muted-foreground">
    {thinking && !busy.length && <span className="flex items-center gap-1"><LoaderCircleIcon className="size-3 animate-spin" />Thinking…</span>}
    {busy.map((c, index) => <span key={index} className="flex items-center gap-1"><LoaderCircleIcon className="size-3 animate-spin" />{running(c.call.name)}</span>)}
    {shown.length > busy.length && <div className="flex flex-wrap items-center gap-1">
      {shown.filter(c => c.done).map((c, index) => <button key={index} type="button" onClick={() => setOpen(c.call)} className={CHIP}>
        <WrenchIcon className="size-3" />{c.call.name}{summary(c.call) && <span className="font-sans">· {summary(c.call)}</span>}
      </button>)}
    </div>}
    <CallDialog call={open} onClose={() => setOpen(null)} />
  </div>
}

function Block({ value }: { value: any }) {
  return <pre className="max-h-64 overflow-auto whitespace-pre-wrap break-words rounded border bg-muted/50 p-2 text-xs">{typeof value === "string" ? value : JSON.stringify(value, null, 2)}</pre>
}

function CallDialog({ call, onClose }: { call: PlaybookToolCall | null, onClose: () => void }) {
  return <Dialog open={!!call} onOpenChange={open => !open && onClose()}>
    <DialogContent className="sm:max-w-2xl">
      <DialogHeader>
        <DialogTitle className="font-mono">{call?.name}</DialogTitle>
      </DialogHeader>
      <div className="min-h-0 space-y-3 overflow-auto">
        <div className="space-y-1">
          <div className="text-sm font-medium">Arguments</div>
          <Block value={call?.args ?? {}} />
        </div>
        <div className="space-y-1">
          <div className="text-sm font-medium">{Array.isArray(call?.result) ? `Result · ${call.result.length}` : "Result"}</div>
          {call?.result === undefined ? <p className="text-sm text-muted-foreground">Nothing went back to the model.</p>
            : Array.isArray(call.result) ? call.result.map((entry, index) => <Block key={index} value={entry} />) : <Block value={call.result} />}
        </div>
      </div>
    </DialogContent>
  </Dialog>
}
